import { ICouponFilterBody, ICouponListingResults } from "@/api_framework/api_modals/coupon";
import InputWithAddOn from "@/app/components/ui/Inputs/InputWithAddOn";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import PaginationComponent from "@/app/components/ui/Pagination";
import TableComponent, { type TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { useStaffClinicListAPI } from "@/app/hooks/api_hooks/clinic/useStaffClinicListAPI";
import { useConfigurationCityListAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationCityListAPI";
import { useCouponCouponTypeListAPI } from "@/app/hooks/api_hooks/coupon/useCouponCouponTypeListAPI";
import { useCouponListingAPI } from "@/app/hooks/api_hooks/coupon/useCouponListingAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useUtils } from "@/app/hooks/useUtils";
import { IConfigurationListState } from "@/app_redux/reducers/slices/auth/configuration_slice";
import { ICouponServiceState } from "@/app_redux/reducers/slices/auth/coupon_service_slice";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { FaRegEdit } from "react-icons/fa";
import { TbCircleLetterC } from "react-icons/tb";
import { useNavigate } from "react-router-dom";

const PAGE_SIZE = 15;

export interface ICouponListingFilterFields {
  status: string;
  membership_coupon: string[];
  cities: string[];
  clinics: string[];
  coupon_type: string[];
}

const statusList = [
  {
    id: "1",
    title: "ACTIVE",
  },
  {
    id: "2",
    title: "INACTIVE",
  },
];

const membershipCouponList = [
  {
    id: "1",
    title: "YES",
  },
  {
    id: "2",
    title: "NO",
  },
];

const CouponListComponent = () => {
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("COUPON_SERVICE", "id");
  const routeIdNew = getRouteKey("INVENTORY", "id");
  const { routeState } = useRouteInfo(routeId);
  const { routeState: routeStateNew } = useRouteInfo(routeIdNew);
  const { couponListing } = routeState.state as ICouponServiceState;
  const couponResults = couponListing?.results || [];
  const totalRecords = couponListing?.total_records || 0;
  const { NO_PWL_RESULTS } = useImageConstants();
  const navigation = useNavigate();
  const { veticDateFormat } = useUtils();
  const isVendorListInProgess = useLoader("isCouponServiceListInProgress");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const { routeState: configurationRouteState } = useRouteInfo(getRouteKey("CONFIGURATION", "id"));
  const { cityList } = configurationRouteState.state as IConfigurationListState;
  const { clinics } = routeStateNew.state as IInventoryState;
  const cityItems = useSelectMenuReducer(cityList, "name", "id");
  const fetchOrgCities = useConfigurationCityListAPI();
  const clinicsOrgItem = useSelectMenuReducer(clinics, "display_name", "id");
  const fetchPWLClinics = useStaffClinicListAPI("INVENTORY");
  const { execute: fetchCouponType, couponType } = useCouponCouponTypeListAPI();
  const couponTypeItems = useSelectMenuReducer(couponType, "value", "id");
  const { execute: fetchCouponsList } = useCouponListingAPI();
  const [filtersReady, setFiltersReady] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const formHook = useForm<ICouponListingFilterFields>({
    mode: "onChange",
    defaultValues: {
      status: "ACTIVE",
      clinics: [],
      coupon_type: [],
    },
  });

  useEffect(() => {
    fetchOrgCities();
    fetchCouponType();
  }, []);

  const selectedCities = useWatch({
    control: formHook.control,
    name: "cities",
  });

  useEffect(() => {
    if (!selectedCities?.length) return;
    fetchPWLClinics({
      cityIds: selectedCities,
      withProviders: false,
    });
  }, [JSON.stringify(selectedCities)]);

  const getCouponFilterPayload = (exportCSV = false): ICouponFilterBody => {
    const formData = formHook.getValues();
    return {
      status: formData?.status || "",
      is_membership_coupon: formData?.membership_coupon || [],
      city_ids: formData?.cities || [],
      clinic_ids: formData.clinics?.length ? formData.clinics : clinicsOrgItem.map((c) => c.id),
      coupon_types: formData.coupon_type?.length
        ? formData.coupon_type.map(String)
        : couponTypeItems.map((c) => String(c.id)),
      export_csv: exportCSV,
    };
  };

  const fetchCouponListingData = (page = currentPage, search = searchQuery, exportCSV = false) => {
    fetchCouponsList(getCouponFilterPayload(exportCSV), search, page, PAGE_SIZE.toString(), exportCSV);
  };

  useEffect(() => {
    if (!clinicsOrgItem?.length || !couponTypeItems?.length) return;
    // const allClinicIds = clinicsOrgItem.map((c) => c.id);
    // const allCouponTypeIds = couponTypeItems.map((c) => c.id);
    // formHook.setValue("clinics", allClinicIds, { shouldDirty: false });
    // formHook.setValue("coupon_type", allCouponTypeIds, { shouldDirty: false });
    setFiltersReady(true);
  }, [clinicsOrgItem, couponTypeItems]);

  const didInitialFetch = useRef(false);

  useEffect(() => {
    if (!filtersReady) return;
    if (didInitialFetch.current) return;
    didInitialFetch.current = true;
    fetchCouponListingData(1);
  }, [filtersReady]);

  const handelCouponCreation = () => {
    navigation(`/app/coupon-service/coupon-creation?page_step=details_and_schedule`);
  };

  const handelCouponEdit = (coupon_id: string) => {
    navigation(`/app/coupon-service/coupon-creation?page_step=details_and_schedule&&coupon_id=${coupon_id}`);
  };

  const Submit = (_data: ICouponListingFilterFields) => {
    setCurrentPage(1);
    fetchCouponListingData(1);
  };

  const handleExport = () => {
    setIsExporting(true);
    fetchCouponsList(getCouponFilterPayload(true), searchQuery, 1, PAGE_SIZE.toString(), true).finally(() => {
      setIsExporting(false);
    });
  };

  const columns: TableColumn<ICouponListingResults>[] = [
    {
      id: "coupon_name",
      header: "Coupon Name",
      width: 160,
      minWidth: 120,
      maxWidth: 160,
      className: "text-xs font-semibold text-black whitespace-normal break-all py-4 px-4",
      headerClassName: "text-xs font-semibold text-[#193A6A] py-3.5 px-4",
      cell: ({ row }) => row.coupon_name || "NA",
    },
    {
      id: "coupon_code",
      header: "Coupon Code",
      width: 140,
      minWidth: 120,
      maxWidth: 140,
      className: "text-xs font-medium text-black whitespace-normal break-all py-4 px-4",
      headerClassName: "text-xs font-semibold text-[#193A6A] py-3.5 px-4",
      cell: ({ row }) => row.coupon_code || "NA",
    },
    {
      id: "coupon_type",
      header: "Coupon Type",
      width: 150,
      minWidth: 120,
      maxWidth: 150,
      className: "text-xs font-medium text-black whitespace-normal break-words py-4 px-4",
      headerClassName: "text-xs font-semibold text-[#193A6A] py-3.5 px-4",
      cell: ({ row }) => row.coupon_type || "NA",
    },
    {
      id: "start_date_time",
      header: "Start Date & Time",
      width: 160,
      minWidth: 140,
      className: "text-xs font-medium text-black whitespace-normal py-4 px-4",
      headerClassName: "text-xs font-semibold text-[#193A6A] py-3.5 px-4",
      cell: ({ row }) => (row.start_date_time ? veticDateFormat(row.start_date_time) : "NA"),
    },
    {
      id: "end_date_time",
      header: "End Date & Time",
      width: 160,
      minWidth: 140,
      className: "text-xs font-medium text-black whitespace-normal py-4 px-4",
      headerClassName: "text-xs font-semibold text-[#193A6A] py-3.5 px-4",
      cell: ({ row }) => (row.end_date_time ? veticDateFormat(row.end_date_time) : "NA"),
    },
    {
      id: "created_by_on",
      header: "Created By & On",
      width: 180,
      minWidth: 160,
      className: "text-xs font-medium text-black whitespace-normal py-4 px-4",
      headerClassName: "text-xs font-semibold text-[#193A6A] py-3.5 px-4",
      cell: ({ row }) => (
        <div className="flex flex-col gap-0.5">
          <span className="font-normal text-black">{row.created_by || "NA"}</span>
          <span className="text-black text-[10px]">{veticDateFormat(row.created_at) || "NA"}</span>
        </div>
      ),
    },
    {
      id: "is_visible",
      header: "Visible on App",
      align: "center",
      width: 100,
      minWidth: 80,
      className: "text-xs font-medium text-black py-4 px-4",
      headerClassName: "text-xs font-semibold text-[#193A6A] py-3.5 text-center px-4",
      cell: ({ row }) => (
        <span className={row.is_visible ? "text-green-600 font-semibold" : "text-red-500 font-medium"}>
          {row.is_visible ? "Yes" : "No"}
        </span>
      ),
    },
    {
      id: "vms_visible",
      header: "Visible on VMS",
      align: "center",
      width: 100,
      minWidth: 80,
      className: "text-xs font-medium text-black py-4 px-4",
      headerClassName: "text-xs font-semibold text-[#193A6A] py-3.5 text-center px-4",
      cell: ({ row }) => (
        <span className={row.vms_visible ? "text-green-600 font-semibold" : "text-red-500 font-medium"}>
          {row.vms_visible ? "Yes" : "No"}
        </span>
      ),
    },
    {
      id: "status",
      header: "Status",
      align: "center",
      width: 100,
      minWidth: 80,
      className: "text-xs font-medium text-black py-4 text-center px-4",
      headerClassName: "text-xs font-semibold text-[#193A6A] py-3.5 text-center px-4",
      cell: ({ row }) => {
        const isActive = row.status?.toLowerCase() === "active";
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10.5px] font-semibold ${isActive ? "bg-green-50 text-green-700 border border-green-100" : "bg-red-50 text-red-700 border border-red-100"}`}
          >
            {row.status || "NA"}
          </span>
        );
      },
    },
    {
      id: "action",
      header: "Action",
      align: "center",
      width: 80,
      minWidth: 70,
      className: "text-xs font-medium text-black py-4 px-4",
      headerClassName: "text-xs font-semibold text-center text-[#193A6A] py-3.5 px-4",
      cell: ({ row }) => (
        <div className="flex items-center justify-center">
          <button
            onClick={() => handelCouponEdit(row.coupon_id)}
            className="text-indigo-600 hover:text-indigo-900 transition-colors p-1.5 hover:bg-indigo-50 rounded-lg"
          >
            <FaRegEdit className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8 mt-2 py-4 bg-white">
      <div>
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto mb-3">
            <h1 className="text-base font-semibold leading-6 text-black">Listed Coupons</h1>
          </div>
        </div>

        {/* Filters Panel */}
        <div className="mt-3 py-2 mb-2">
          <form onSubmit={formHook.handleSubmit(Submit)} className="w-full">
            <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
              <div className="w-full sm:w-[180px]">
                <SearchableSelectMenu
                  errorMessages={[{ message: "", type: "required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      formHook.setValue("status", item.title);
                    }
                  }}
                  fieldError={formHook.formState.errors.status}
                  register={formHook.register("status", { required: true })}
                  selectItems={statusList}
                  placeholder="Select Status"
                  showTooltips={false}
                  showTypedErrors={false}
                  showDropdownIcon={true}
                  defaultSelected={statusList?.filter((oc) => oc.title === formHook.watch("status"))[0]}
                  listBoxClassName="w-full"
                  className="text-gray-800"
                  label="Status"
                />
              </div>
              <div className="w-full sm:w-[200px]">
                <SearchableMultiSelectMenu
                  onSelectItem={(items) => {
                    const membershipCouponId = items?.map((i) => i?.id);
                    formHook.setValue("membership_coupon", membershipCouponId);
                  }}
                  errorMessages={[{ message: "", type: "required" }]}
                  fieldError={formHook.formState.errors.membership_coupon}
                  register={formHook.register("membership_coupon", {
                    required: true,
                  })}
                  withSelectAll
                  defaultSelectAll
                  showTooltips={true}
                  showTypedErrors={false}
                  placeholder="Select Membership Coupon"
                  defaultSelected={membershipCouponList.filter((c) =>
                    formHook.watch("membership_coupon")?.includes(c.id),
                  )}
                  selectItems={membershipCouponList}
                  label="Membership Coupon"
                />
              </div>
              <div className="w-full sm:w-[200px]">
                <SearchableMultiSelectMenu
                  withSelectAll
                  label="Cities"
                  defaultSelectAll
                  errorMessages={[{ type: "required", message: "City is required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      const cityId = item.map((i) => i.id);
                      formHook.setValue("cities", cityId);
                    }
                  }}
                  fieldError={formHook.formState.errors.cities}
                  register={formHook.register("cities", { required: true })}
                  selectItems={cityItems}
                  placeholder="Select Cities"
                  showTooltips={true}
                  showTypedErrors={false}
                  showDropdownIcon={true}
                  defaultSelected={cityItems?.filter((c) => formHook.watch("cities")?.includes(c.id))}
                  className="text-gray-800"
                  containerClassName="w-full"
                />
              </div>
              <div className="w-full sm:w-[200px]">
                <SearchableMultiSelectMenu
                  withSelectAll
                  label="Clinics"
                  defaultSelectAll
                  errorMessages={[{ type: "required", message: "Clinic is required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      const clincId = item.map((i) => i.id);
                      formHook.setValue("clinics", clincId);
                    }
                  }}
                  fieldError={formHook.formState.errors.clinics}
                  register={formHook.register("clinics", { required: true })}
                  selectItems={clinicsOrgItem}
                  placeholder="Select Clinics"
                  showTooltips={true}
                  showTypedErrors={false}
                  showDropdownIcon={true}
                  defaultSelected={clinicsOrgItem?.filter((c) => formHook.watch("clinics")?.includes(c.id))}
                  className="text-gray-800"
                  containerClassName="w-full"
                />
              </div>
              <div className="w-full sm:w-[200px]">
                <SearchableMultiSelectMenu
                  withSelectAll
                  label="Coupon Type"
                  defaultSelectAll
                  errorMessages={[{ type: "required", message: "Coupon Type is required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      const couponTypeId = item.map((i) => i.id);
                      formHook.setValue("coupon_type", couponTypeId);
                    }
                  }}
                  fieldError={formHook.formState.errors.coupon_type}
                  register={formHook.register("coupon_type", { required: true })}
                  selectItems={couponTypeItems}
                  placeholder="Select Coupon Type"
                  showTooltips={true}
                  showTypedErrors={false}
                  showDropdownIcon={true}
                  defaultSelected={couponTypeItems?.filter((c) => formHook.watch("coupon_type")?.includes(c.id))}
                  className="text-gray-800"
                  containerClassName="w-full"
                />
              </div>
              <div className="w-full sm:w-auto">
                <button
                  type="submit"
                  className="bg-black hover:bg-gray-800 transition-colors px-6 py-2 border-transparent rounded-lg text-sm text-white font-semibold cursor-pointer shadow-sm"
                >
                  Search
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Search & Actions Bar */}
        <div className="my-4 flex flex-wrap items-center gap-3 w-full justify-between">
          <div className="flex-1 min-w-[200px] max-w-sm">
            <InputWithAddOn
              onEnterKeyPress={(query: string) => {
                setSearchQuery(query);
                setCurrentPage(1);
                fetchCouponListingData(1, query);
              }}
              containerClassName="w-full"
              addon={<TbCircleLetterC className="w-4 h-4 text-gray-500" />}
              placeholder="Search by Name, Code"
              onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
                const value = (e.target as HTMLInputElement).value;
                if (value === "") {
                  setSearchQuery("");
                  setCurrentPage(1);
                  fetchCouponListingData(1, "");
                }
              }}
            />
          </div>
          <div className="flex gap-3 items-center">
            <div className="flex-shrink-0">
              <button
                onClick={handelCouponCreation}
                className="bg-black hover:bg-gray-800 transition-colors text-white font-semibold text-xs py-2 px-5 rounded-lg cursor-pointer whitespace-nowrap shadow-sm"
              >
                Create Coupon
              </button>
            </div>
            <div className="flex-shrink-0">
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="bg-black hover:bg-gray-800 transition-colors text-white font-semibold text-xs py-2 px-8 rounded-lg cursor-pointer whitespace-nowrap shadow-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isExporting && (
                  <svg
                    className="animate-spin h-3 w-3 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                {isExporting ? "Exporting..." : "Export CSV"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Listing Table Container */}
      <div className="mt-4 flow-root">
        {isVendorListInProgess ? (
          <TableSkeleton />
        ) : couponResults?.length === 0 ? (
          <NoDataFoundComponent
            imageURL={NO_PWL_RESULTS}
            message="No Coupon Available"
            description="No coupons have been available, please onboard some coupons through create coupon."
          />
        ) : (
          <div>
            <TableComponent<ICouponListingResults>
              columns={columns}
              data={couponResults}
              getRowId={(row) => row.coupon_id}
              containerClassName="border border-gray-200/80 shadow-sm rounded-2xl mr-0 bg-white"
              scrollContainerClassName="overflow-x-auto mt-0 max-h-[calc(100vh-280px)]"
              minTableWidthClassName="min-w-full"
              tableClassName="min-w-full divide-y divide-gray-200"
              stickyHeader={true}
              wrapCells={true}
            />
            <div className="flex items-center justify-center my-4">
              <PaginationComponent
                className="pagination-bar"
                currentPage={currentPage}
                pageSize={PAGE_SIZE}
                totalCount={totalRecords}
                onPageChange={(pageNumber) => {
                  setCurrentPage(pageNumber);
                  fetchCouponListingData(pageNumber);
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CouponListComponent;
